import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { loadFonts } from "./fonts/roboto";
import { calculateAvailableAfterPlanned } from "./finance";

function formatPLN(value) {
  return new Intl.NumberFormat("pl-PL", {
    style: "currency",
    currency: "PLN",
  }).format(Number(value ?? 0));
}

function formatDate(dateStr) {
  if (!dateStr) return "—";

  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "—";

  return new Intl.DateTimeFormat("pl-PL", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
}

function calcSummary(expenses = []) {
  const totalValue = expenses.reduce(
    (sum, e) => sum + Number(e.total_value ?? 0),
    0
  );

  const toReturn = expenses
    .filter((e) => e.status === "Zakupione / Nie wypłacone")
    .reduce((sum, e) => sum + Number(e.total_value ?? 0), 0);

  const paidOut = expenses
    .filter((e) => e.status === "Wypłacone")
    .reduce(
      (sum, e) => sum + Number(e.payout_amount ?? e.total_value ?? 0),
      0
    );

  const realized = expenses
    .filter(
      (e) =>
        e.status === "Wypłacone" ||
        e.status === "Zakupione / Nie wypłacone"
    )
    .reduce((sum, e) => sum + Number(e.total_value ?? 0), 0);

  return {
    count: expenses.length,
    totalValue,
    toReturn,
    paidOut,
    realized,
  };
}

function buildRows(expenses = []) {
  return expenses.map((e, index) => [
    index + 1,
    e.occasion ?? "—",
    formatDate(e.purchase_date),
    e.item_or_service ?? "—",
    e.quantity ?? "—",
    formatPLN(e.total_value ?? 0),
    e.status ?? "—",
    e.receipt_link
      ? { content: "Podgląd", url: e.receipt_link }
      : "—",
  ]);
}

function drawPageBackground(doc) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  doc.setFillColor(247, 252, 250);
  doc.rect(0, 0, pageWidth, pageHeight, "F");

  doc.setDrawColor(214, 245, 234);
  doc.setLineWidth(0.4);
  doc.line(0, 78, pageWidth, 78);

  doc.setDrawColor(253, 236, 213);
  doc.setLineWidth(0.35);
  doc.line(0, 82, pageWidth, 82);
}

function drawHeader(doc, { title, className, schoolYear, today, summaryCount }) {
  const pageWidth = doc.internal.pageSize.getWidth();

  doc.setFillColor(239, 249, 245);
  doc.roundedRect(8, 8, pageWidth - 16, 24, 6, 6, "F");

  doc.setFillColor(253, 246, 227);
  doc.roundedRect(pageWidth - 64, 8, 56, 24, 6, 6, "F");

  doc.setTextColor(16, 24, 40);
  doc.setFont("Roboto", "bold");
  doc.setFontSize(17);
  doc.text(title, 16, 18);

  doc.setTextColor(4, 120, 87);
  doc.setFont("Roboto", "normal");
  doc.setFontSize(9);
  doc.text(`${className}${schoolYear ? ` • ${schoolYear}` : ""}`, 16, 25);

  doc.setTextColor(71, 85, 105);
  doc.setFont("Roboto", "normal");
  doc.setFontSize(8.5);
  doc.text(`Data wygenerowania: ${today}`, pageWidth - 16, 18, {
    align: "right",
  });
  doc.text(`Liczba pozycji: ${summaryCount}`, pageWidth - 16, 24, {
    align: "right",
  });
}

function drawStatCard(
  doc,
  {
    x,
    y,
    w,
    h,
    label,
    value,
    sub,
    valueColor = [15, 23, 42],
    accentColor = null,
  }
) {
  doc.setDrawColor(230, 238, 235);
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(x, y, w, h, 4, 4, "FD");

  if (accentColor) {
    doc.setFillColor(...accentColor);
    doc.roundedRect(x, y, 2.2, h, 2, 2, "F");
  }

  doc.setTextColor(148, 163, 184);
  doc.setFont("Roboto", "bold");
  doc.setFontSize(7.5);
  doc.text(label, x + 5, y + 6.5);

  doc.setTextColor(...valueColor);
  doc.setFont("Roboto", "bold");
  doc.setFontSize(12.5);
  doc.text(value, x + 5, y + 14.5);

  if (sub) {
    doc.setTextColor(100, 116, 139);
    doc.setFont("Roboto", "normal");
    doc.setFontSize(7);
    doc.text(sub, x + 5, y + 19.5, {
      maxWidth: w - 10,
    });
  }
}

function drawInfoNote(doc, { x, y, w, text }) {
  doc.setDrawColor(209, 250, 229);
  doc.setFillColor(236, 253, 245);
  doc.roundedRect(x, y, w, 9, 3, 3, "FD");

  doc.setTextColor(5, 150, 105);
  doc.setFont("Roboto", "normal");
  doc.setFontSize(7.5);
  doc.text(text, x + 4, y + 5.8, {
    maxWidth: w - 8,
  });
}

function drawCollectionBox(
  doc,
  {
    x,
    y,
    w,
    h,
    activeCollectionName,
    activeCollectionCurrentAmount,
    activeCollectionTargetAmount,
  }
) {
  doc.setDrawColor(230, 238, 235);
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(x, y, w, h, 4, 4, "FD");

  doc.setTextColor(15, 23, 42);
  doc.setFont("Roboto", "bold");
  doc.setFontSize(10);
  doc.text("Aktywna zbiórka", x + 4, y + 7);

  const hasCollection = Boolean(activeCollectionName);
  const current = Number(activeCollectionCurrentAmount ?? 0);
  const target = Number(activeCollectionTargetAmount ?? 0);
  const progress =
    hasCollection && target > 0
      ? Math.max(0, Math.min(100, (current / target) * 100))
      : 0;
  const missing = hasCollection ? Math.max(0, target - current) : 0;

  doc.setFont("Roboto", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(71, 85, 105);
  doc.text(
    `Nazwa: ${hasCollection ? activeCollectionName : "Brak aktywnej zbiórki"}`,
    x + 4,
    y + 14
  );

  if (hasCollection) {
    doc.text(
      `Zebrano: ${formatPLN(current)} / ${formatPLN(target)}`,
      x + 4,
      y + 21
    );
    doc.text(`Brakuje do celu: ${formatPLN(missing)}`, x + 4, y + 28);
    doc.text(`Realizacja: ${progress.toFixed(1)}%`, x + w - 28, y + 28);

    const barX = x + 4;
    const barY = y + 33;
    const barW = w - 8;
    const barH = 5;

    doc.setFillColor(237, 242, 247);
    doc.roundedRect(barX, barY, barW, barH, 2, 2, "F");

    const progressWidth = (barW * progress) / 100;
    if (progressWidth > 0) {
      doc.setFillColor(45, 212, 191);
      doc.roundedRect(barX, barY, progressWidth, barH, 2, 2, "F");

      const accentStart = Math.max(barX, barX + progressWidth - 18);
      const accentWidth = Math.min(18, progressWidth);
      doc.setFillColor(250, 204, 21);
      doc.roundedRect(accentStart, barY, accentWidth, barH, 2, 2, "F");
    }
  }
}

function drawBarChart(
  doc,
  {
    x,
    y,
    w,
    h,
    availableFunds,
    plannedTotal = 0,
    toReturn,
    paidOut,
  }
) {
  doc.setDrawColor(230, 238, 235);
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(x, y, w, h, 4, 4, "FD");

  doc.setTextColor(15, 23, 42);
  doc.setFont("Roboto", "bold");
  doc.setFontSize(10);
  doc.text("Wykres porównawczy", x + 4, y + 7);

  const values = [
    {
      label: "Dostępne",
      value: Math.max(0, Number(availableFunds ?? 0)),
      color: [16, 185, 129],
    },
    {
      label: "Planowane",
      value: Math.max(0, Number(plannedTotal ?? 0)),
      color: [245, 158, 11],
    },
    {
      label: "Zwrot",
      value: Math.max(0, Number(toReturn ?? 0)),
      color: [251, 146, 60],
    },
    {
      label: "Wypłacone",
      value: Math.max(0, Number(paidOut ?? 0)),
      color: [59, 130, 246],
    },
  ];

  const maxValue = Math.max(...values.map((v) => v.value), 1);

  const chartX = x + 10;
  const chartY = y + h - 12;
  const chartH = h - 24;
  const barW = 14;
  const gap = 12;

  values.forEach((item, index) => {
    const barX = chartX + index * (barW + gap);
    const barHeight = (item.value / maxValue) * chartH;
    const barY = chartY - barHeight;

    doc.setFillColor(...item.color);
    doc.roundedRect(barX, barY, barW, barHeight, 1.5, 1.5, "F");

    doc.setTextColor(100, 116, 139);
    doc.setFont("Roboto", "normal");
    doc.setFontSize(7);
    doc.text(item.label, barX + barW / 2, chartY + 5, { align: "center" });

    doc.setFontSize(6.7);
    doc.text(formatPLN(item.value), barX + barW / 2, barY - 2, {
      align: "center",
      maxWidth: 24,
    });
  });
}

export async function exportExpensesPdf({
  expenses = [],
  className = "Klasa",
  schoolYear = "",
  title = "Rozliczenie wydatków",
  fileName,
  availableFunds = 0,
  plannedExpensesTotal = 0,
  availableAfterPlanned = null,
  reimbursementAmount = null,
  activeCollectionName = null,
  activeCollectionCurrentAmount = 0,
  activeCollectionTargetAmount = 0,
}) {
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
  });

  await loadFonts(doc);

  const pageWidth = doc.internal.pageSize.getWidth();

  const today = new Intl.DateTimeFormat("pl-PL", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());

  const summary = calcSummary(expenses);
  const rows = buildRows(expenses);
  const summaryToReturn = Number(reimbursementAmount ?? summary.toReturn);
  const plannedTotal = Number(plannedExpensesTotal ?? 0);
  const currentBalance = Number(availableFunds ?? 0) + summaryToReturn;
  const availableAfterPlannedValue =
    availableAfterPlanned ??
    calculateAvailableAfterPlanned({
      currentBalance,
      reimbursementAmount: summaryToReturn,
      plannedExpensesTotal: plannedTotal,
    });

  const allSettled =
    summary.totalValue > 0 &&
    summary.totalValue === summary.paidOut &&
    summary.totalValue === summary.realized &&
    summary.toReturn === 0;

  const safeFileName =
    fileName ||
    `rozliczenie-wydatkow-${today.replace(/\./g, "-")}.pdf`;

  drawPageBackground(doc);
  drawHeader(doc, {
    title,
    className,
    schoolYear,
    today,
    summaryCount: summary.count,
  });

  const dashboardTop = 38;
  const marginX = 14;
  const gap = 4;
  const cardW = (pageWidth - marginX * 2 - gap * 2) / 3;
  const cardH = 22;

  drawStatCard(doc, {
    x: marginX,
    y: dashboardTop,
    w: cardW,
    h: cardH,
    label: "Saldo bieżące",
    value: formatPLN(currentBalance),
    sub: "Aktualny stan konta przed odjęciem zwrotów i wydatków planowanych.",
    valueColor: [5, 150, 105],
    accentColor: [16, 185, 129],
  });

  drawStatCard(doc, {
    x: marginX + cardW + gap,
    y: dashboardTop,
    w: cardW,
    h: cardH,
    label: "Do zwrotu skarbnikowi",
    value: formatPLN(summaryToReturn),
    sub: "Zakupy opłacone prywatnie, które czekają na zwrot.",
    valueColor: [217, 119, 6],
    accentColor: [245, 158, 11],
  });

  drawStatCard(doc, {
    x: marginX + (cardW + gap) * 2,
    y: dashboardTop,
    w: cardW,
    h: cardH,
    label: "Dostępne środki",
    value: formatPLN(availableAfterPlannedValue),
    sub: "Kwota do dyspozycji po odjęciu zwrotów dla skarbnika oraz wydatków planowanych.",
    valueColor:
      availableAfterPlannedValue < 0 ? [217, 119, 6] : [5, 150, 105],
    accentColor:
      availableAfterPlannedValue < 0 ? [245, 158, 11] : [45, 212, 191],
  });

  const secondRowY = dashboardTop + cardH + gap;

  drawStatCard(doc, {
    x: marginX,
    y: secondRowY,
    w: cardW,
    h: cardH,
    label: "Wydatki planowane",
    value: formatPLN(plannedTotal),
    sub: "Zaplanowane koszty, które nie zostały jeszcze rozliczone jako wydatki zrealizowane.",
    valueColor: [217, 119, 6],
    accentColor: [245, 158, 11],
  });

  drawStatCard(doc, {
    x: marginX + cardW + gap,
    y: secondRowY,
    w: cardW,
    h: cardH,
    label: "Wypłacone środki",
    value: formatPLN(summary.paidOut),
    sub: "Kwota faktycznie wypłacona przez skarbnika.",
    valueColor: [15, 23, 42],
    accentColor: [59, 130, 246],
  });

  drawStatCard(doc, {
    x: marginX + (cardW + gap) * 2,
    y: secondRowY,
    w: cardW,
    h: cardH,
    label: "Wydatki zrealizowane",
    value: formatPLN(summary.realized),
    sub: "Zakupy wykonane: wypłacone lub oczekujące na zwrot.",
    valueColor: [15, 23, 42],
    accentColor: [16, 185, 129],
  });

  const thirdRowY = secondRowY + cardH + gap;

  drawStatCard(doc, {
    x: marginX,
    y: thirdRowY,
    w: cardW,
    h: cardH,
    label: schoolYear
      ? `Całkowite wydatki klasy w roku szkolnym ${schoolYear}`
      : "Całkowite wydatki klasy",
    value: formatPLN(summary.totalValue),
    sub: "Suma wszystkich wydatków dodanych do rozliczeń.",
    valueColor: [15, 23, 42],
    accentColor: [16, 185, 129],
  });

  drawStatCard(doc, {
    x: marginX + cardW + gap,
    y: thirdRowY,
    w: cardW,
    h: cardH,
    label: "Status zbiórki",
    value: activeCollectionName ? "Aktywna" : "Brak aktywnej",
    sub: activeCollectionName || undefined,
    valueColor: activeCollectionName ? [5, 150, 105] : [100, 116, 139],
    accentColor: activeCollectionName ? [16, 185, 129] : [203, 213, 225],
  });

  let noteY = thirdRowY + cardH + 4;
  if (allSettled) {
    drawInfoNote(doc, {
      x: marginX,
      y: noteY,
      w: pageWidth - marginX * 2,
      text: "Wszystkie dotychczasowe wydatki zostały już opłacone i rozliczone.",
    });
    noteY += 12;
  }

  const sectionY = noteY + 2;
  const boxGap = 6;
  const leftBoxW = (pageWidth - marginX * 2 - boxGap) * 0.56;
  const rightBoxW = pageWidth - marginX * 2 - boxGap - leftBoxW;
  const sectionH = 42;

  drawCollectionBox(doc, {
    x: marginX,
    y: sectionY,
    w: leftBoxW,
    h: sectionH,
    activeCollectionName,
    activeCollectionCurrentAmount,
    activeCollectionTargetAmount,
  });

  drawBarChart(doc, {
    x: marginX + leftBoxW + boxGap,
    y: sectionY,
    w: rightBoxW,
    h: sectionH,
    availableFunds: availableAfterPlannedValue,
    plannedTotal,
    toReturn: summaryToReturn,
    paidOut: summary.paidOut,
  });

  autoTable(doc, {
    startY: sectionY + sectionH + 8,
    margin: { left: 14, right: 14, top: 35 },

    head: [[
      "Lp.",
      "Okazja",
      "Data",
      "Towar / usługa",
      "Ilość",
      "Łączna wartość",
      "Status",
      "Paragon",
    ]],
    body: rows,
    theme: "grid",

    styles: {
      font: "Roboto",
      fontSize: 7.6,
      cellPadding: 2.2,
      lineColor: [231, 237, 235],
      lineWidth: 0.1,
      textColor: [30, 41, 59],
      valign: "middle",
      overflow: "linebreak",
      fillColor: [255, 255, 255],
    },

    headStyles: {
      fillColor: [241, 248, 245],
      textColor: [15, 23, 42],
      font: "Roboto",
      fontStyle: "bold",
      lineColor: [220, 231, 227],
    },

    alternateRowStyles: {
      fillColor: [249, 252, 251],
    },

    columnStyles: {
      0: { cellWidth: 12, halign: "center" },
      1: { cellWidth: 42 },
      2: { cellWidth: 24, halign: "center" },
      3: { cellWidth: 64 },
      4: { cellWidth: 16, halign: "center" },
      5: { cellWidth: 30, halign: "right" },
      6: { cellWidth: 42, fontSize: 7.2 },
      7: { cellWidth: 24, halign: "center" },
    },

    didParseCell: (data) => {
      if (
        data.section === "body" &&
        data.column.index === 7 &&
        data.cell.raw?.url
      ) {
        data.cell.styles.textColor = [5, 150, 105];
        data.cell.styles.fontStyle = "bold";
      }
    },

    didDrawCell: (data) => {
      if (
        data.section === "body" &&
        data.column.index === 7 &&
        data.cell.raw?.url
      ) {
        doc.link(data.cell.x, data.cell.y, data.cell.width, data.cell.height, {
          url: data.cell.raw.url,
        });
      }
    },

    willDrawPage: (data) => {
      if (data.pageNumber > 1) {
        drawPageBackground(doc);
        drawHeader(doc, {
          title,
          className,
          schoolYear,
          today,
          summaryCount: summary.count,
        });
      }
    },

    didDrawPage: () => {
      const currentPageHeight = doc.internal.pageSize.getHeight();
      const currentPageWidth = doc.internal.pageSize.getWidth();

      doc.setFont("Roboto", "normal");
      doc.setFontSize(8);
      doc.setTextColor(100, 116, 139);

      doc.text(
        "Szczegółowe informacje są dostępne po zalogowaniu do aplikacji. Jeśli paragon został dodany, link znajduje się w kolumnie Paragon.",
        14,
        currentPageHeight - 8,
        { maxWidth: currentPageWidth - 48 }
      );

      const pageNumber = doc.getCurrentPageInfo().pageNumber;
      doc.text(`Strona ${pageNumber}`, currentPageWidth - 14, currentPageHeight - 8, {
        align: "right",
      });
    },
  });

  doc.save(safeFileName);
}
